/**
 * Authentication Service
 * Centralizes authentication logic and HCP verification flow
 *
 * Handles the LoginV2 workflow configuration from JSON style files:
 * - loginV2.hcpVerification.enabled
 * - loginV2.authenticate
 * - loginV2.type (npi | userpassword | key | npiModal)
 */

/**
 * Checks if loginV2 workflow configuration exists
 * Used to determine if LoginV2 workflow should be used vs legacy workflow
 *
 * @param {Object} settings - Application settings
 * @returns {boolean} True if loginV2 object exists
 */
export const hasLoginV2Workflow = (settings) => {
  return !!(settings && settings.loginV2 && typeof settings.loginV2 === 'object');
}

/**
 * Determines if HCP verification is required
 * Checks loginV2.hcpVerification.enabled first, falls back to loginModal.disabled
 * Only applies to LoginV2 workflow - legacy workflow uses LoginModal component directly
 *
 * @param {Object} settings - Application settings
 * @returns {boolean} True if HCP verification should be shown
 */
export const requiresHcpVerification = (settings) => {
  // Only check loginV2 if LoginV2 workflow exists
  if (!hasLoginV2Workflow(settings)) {
    // Legacy workflow - return false (handled by LoginModal component)
    return false;
  }

  const loginV2 = settings.loginV2 || {};
  const hcpVerification = loginV2.hcpVerification || {};

  // Check loginV2 configuration
  if (loginV2.hasOwnProperty('hcpVerification')) {
    return hcpVerification.enabled !== false;
  }

  // Default to true if not specified in LoginV2 workflow
  return true;
}

/**
 * Determines if authentication is required
 * Checks loginV2.authenticate first, falls back to authenticate
 * Only applies to LoginV2 workflow - legacy workflow uses settings.authenticate directly
 *
 * @param {Object} settings - Application settings
 * @returns {boolean} True if authentication is required
 */
export const requiresAuthentication = (settings) => {
  // Only check loginV2 if LoginV2 workflow exists
  if (!hasLoginV2Workflow(settings)) {
    // Legacy workflow - return false (handled by legacy LoginModal component)
    return false;
  }

  const loginV2 = settings.loginV2 || {};

  if (loginV2.hasOwnProperty('authenticate')) {
    return loginV2.authenticate === true;
  }

  // Default to false for LoginV2 workflow if not specified
  return false;
}

/**
 * Gets the login type from loginV2.type or falls back to loginType
 * Only applies to LoginV2 workflow - legacy workflow uses settings.loginType directly
 *
 * @param {Object} settings - Application settings
 * @returns {string} Login type (npi, userpassword, key, npiModal, etc.)
 */
export const getLoginType = (settings) => {
  // Only check loginV2 if LoginV2 workflow exists
  if (!hasLoginV2Workflow(settings)) {
    // Legacy workflow - return empty string (handled by legacy Login component)
    return '';
  }

  const loginV2 = settings.loginV2 || {};

  if (loginV2.type) {
    return loginV2.type;
  }

  // Default to empty string for LoginV2 workflow if not specified
  return '';
}

/**
 * Checks if login type is npiModal (LoginV2 workflow)
 *
 * @param {Object} settings - Application settings
 * @returns {boolean} True if login type is npiModal
 */
export const isNpiModalType = (settings) => {
  return getLoginType(settings) === 'npiModal';
}

/**
 * Whether an unauthenticated user may load the coverage route (/) under LoginV2.
 * Full-page login types (e.g. npi) with auth required and no HCP must hit /login first;
 * public coverage, npiModal overlays, and HCP-first flows may render / without redirect.
 *
 * @param {Object} settings - Application settings
 * @returns {boolean} True if / is allowed without authentication
 */
export const allowsUnauthenticatedCoverageHome = (settings) => {
  if (!hasLoginV2Workflow(settings)) {
    return false;
  }
  if (!requiresAuthentication(settings)) {
    return true;
  }
  return isNpiModalType(settings) || requiresHcpVerification(settings);
}

/**
 * Checks if login type requires full login page (not modal)
 * Used for flow documentation and determining next step after HCP verification
 * Note: Actual component rendering is handled by PalPage using isNpiModalType()
 *
 * @param {Object} settings - Application settings
 * @returns {boolean} True if login requires full page (legacy Login component)
 */
export const requiresLoginPage = (settings) => {
  const loginType = getLoginType(settings);
  return loginType === 'npi' ||
         loginType === 'userpassword' ||
         loginType === 'key' ||
         loginType === 'npiKey' ||
         loginType === 'userPass' ||
         loginType === 'token';
}

/**
 * Determines the next step after HCP verification
 * Returns: 'COVERAGE_PAGE' | 'LOGIN_MODAL' | 'LOGIN_PAGE'
 *
 * Note: This is used for flow documentation and routing decisions.
 * Actual component rendering (LoginModals vs Login) is handled by PalPage
 * based on isNpiModalType() check.
 *
 * @param {Object} settings - Application settings
 * @returns {string} Next step after verification
 */
export const getNextStepAfterVerification = (settings) => {
  // If authentication not required, go to coverage
  if (!requiresAuthentication(settings)) {
    return 'COVERAGE_PAGE';
  }

  const loginType = getLoginType(settings);

  // If npiModal type, show login in modal (LoginModals component)
  if (loginType === 'npiModal') {
    return 'LOGIN_MODAL';
  }

  // For other login types, show full-page login (legacy Login component)
  // PalPage will render Login component on /login route for non-modal types
  if (requiresLoginPage(settings)) {
    return 'LOGIN_PAGE';
  }

  // Default: no login required, go to coverage
  return 'COVERAGE_PAGE';
}

/**
 * Determines if user should be redirected to login page
 *
 * @param {Object} settings - Application settings
 * @param {boolean} authenticated - Current authentication state
 * @param {string} currentPath - Current route path
 * @returns {boolean} True if should redirect to login
 */
export const shouldRedirectToLogin = (settings, authenticated, currentPath) => {
  if (authenticated) {
    return false;
  }

  if (currentPath === '/login') {
    return false;
  }

  return requiresAuthentication(settings);
}

/**
 * Determines if HCP verification should show on login page or coverage page
 * Modals should show as overlays on coverage page (/) for legacy behavior
 * Only applies to LoginV2 workflow - legacy workflow uses LoginModal component directly
 *
 * @param {Object} settings - Application settings
 * @param {boolean} authenticated - Current authentication state
 * @param {string} currentPath - Current route path
 * @returns {boolean} True if HCP verification should show
 */
export const shouldShowHcpVerificationOnLogin = (settings, authenticated, currentPath) => {
  // Only check loginV2 if LoginV2 workflow exists
  if (!hasLoginV2Workflow(settings)) {
    // Legacy workflow - return false (handled by legacy LoginModal component)
    return false;
  }

  if (authenticated) {
    return false;
  }

  // Show modals on both /login and / routes (coverage page)
  if (currentPath !== '/login' && currentPath !== '/') {
    return false;
  }

  return requiresHcpVerification(settings);
}

/**
 * Determines the initial flow based on configuration
 * Returns: 'HCP_MODAL' | 'LOGIN_MODAL' | 'LOGIN_PAGE' | 'COVERAGE_PAGE'
 *
 * @param {Object} settings - Application settings
 * @param {boolean} authenticated - Current authentication state
 * @param {string} currentPath - Current route path
 * @returns {string} Initial flow state
 */
export const getInitialFlow = (settings, authenticated, currentPath) => {
  // If authenticated, go to coverage
  if (authenticated) {
    return 'COVERAGE_PAGE';
  }

  // If HCP verification disabled
  if (!requiresHcpVerification(settings)) {
    if (requiresAuthentication(settings)) {
      const loginType = getLoginType(settings);
      if (loginType === 'npiModal') {
        return 'LOGIN_MODAL';
      }
      return 'LOGIN_PAGE';
    }
    return 'COVERAGE_PAGE';
  }

  // HCP verification enabled
  if (currentPath === '/login') {
    return 'HCP_MODAL';
  }

  // On other pages, redirect to login for HCP verification
  return 'LOGIN_PAGE';
}

/**
 * Determines if reject button should show in NPI login modal
 * Returns true when HCP verification is disabled but authentication is required
 * Used to show reject button instead of cancel button in NPI modal
 *
 * @param {Object} settings - Application settings
 * @returns {boolean} True if reject button should show
 */
export const shouldShowNpiModalRejectButton = (settings) => {
  // Only check loginV2 if LoginV2 workflow exists
  if (!hasLoginV2Workflow(settings)) {
    // Legacy workflow - return false
    return false;
  }

  // Reject button shows when HCP verification is disabled but authentication is required
  return !requiresHcpVerification(settings) && requiresAuthentication(settings);
}
